import {
  App,
  Checkbox,
  Flex,
  Modal,
  Typography,
} from 'antd'
import { CheckboxChangeEvent } from 'antd/es/checkbox'
import React, {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useState,
} from 'react'

import {
  CreateSalesSettingParams,
  createSalesSettings,
  readSalesSettingsHistory,
  SalesAttribution,
} from '@/redux/sales-settings'
import { useAppDispatch } from '@/redux/utils'

import AttributionDisplay from './attribution-display'
import s from './sales-settings-display.module.css'

const { Title } = Typography

export interface SalesSettingsEditorProps {
  open: boolean
  setOpen: Dispatch<SetStateAction<boolean>>
  defaultValue: CreateSalesSettingParams
}

export default function SalesSettingsEditor({
  open,
  setOpen,
  defaultValue,
}: SalesSettingsEditorProps) {
  const [value, setValue] = useState<CreateSalesSettingParams>(defaultValue)
  const [submitting, setSubmitting] = useState(false)
  const dispatch = useAppDispatch()
  const { message } = App.useApp()
  const onSubmit = useCallback(async () => {
    setSubmitting(true)
    try {
      await dispatch(createSalesSettings({
        ...value,
        messageType: SalesAttribution.MESSAGE_SENDER,
        tipsType: SalesAttribution.LAST_CHATTER,
        postsType: SalesAttribution.MESSAGE_SENDER,
      })).unwrap()
      void message.success('Sales Settings update success')
      void dispatch(readSalesSettingsHistory({}))
      setOpen(false)
    } catch (error) {
      // ignore
    }
    setSubmitting(false)
  }, [dispatch, message, setOpen, value])
  useEffect(() => {
    if (!open) {
      setValue(defaultValue)
    }
  }, [open, defaultValue])
  const toggleCheckboxValue = useCallback((attribute: keyof CreateSalesSettingParams) => ({ target: { checked } }: CheckboxChangeEvent) => {
    setValue((oldValue) => ({ ...oldValue, [attribute]: checked }))
  }, [])
  const onSaleSetHoursChange = useCallback((newSaleSetHours?: number | null) => {
    setValue((oldValue) => ({ ...oldValue, saleSetHours: newSaleSetHours || 0 }))
  }, [])
  const onSaleSetMinutesChange = useCallback((newSaleSetMinutes?: number | null) => {
    setValue((oldValue) => ({ ...oldValue, saleSetMinutes: newSaleSetMinutes || 0 }))
  }, [])

  const tipsChecked = value.tipsMessagesStatus &&
    value.tipsPostsStatus &&
    value.tipsProfileStatus &&
    value.tipsStoriesStatus &&
    value.tipsStreamsStatus
  const toggleTips = useCallback(() => {
    if (tipsChecked) {
      setValue((oldValue) => ({
        ...oldValue,
        tipsMessagesStatus: false,
        tipsPostsStatus: false,
        tipsProfileStatus: false,
        tipsStoriesStatus: false,
        tipsStreamsStatus: false,
      }))
    } else {
      setValue((oldValue) => ({
        ...oldValue,
        tipsMessagesStatus: true,
        tipsPostsStatus: true,
        tipsProfileStatus: true,
        tipsStoriesStatus: true,
        tipsStreamsStatus: true,
      }))
    }
  }, [tipsChecked])

  return (
    <Modal
      open={open}
      onCancel={() => setOpen(false)}
      okText="Save"
      okButtonProps={{ loading: submitting }}
      onOk={onSubmit}
      title="Edit sales settings"
      width={1000}
    >
      <Flex justify="space-between" align="center" style={{ marginBottom: 10 }}>
        <div>
          <Title level={5} style={{ margin: 0 }}>Customize how employee earnings are calculated in
            your reports</Title>
        </div>
      </Flex>
      <table className={s.Table}>
        <tbody>
          <tr>
            <td>
              <Title level={5}>
                <Checkbox
                  checked={value.messageStatus}
                  onChange={toggleCheckboxValue('messageStatus')}
                >
                  Message
                </Checkbox>
              </Title>
            </td>
            <td>
              <AttributionDisplay type="message" />
            </td>
          </tr>
          <tr>
            <td>
              <Title level={5}>
                <Checkbox
                  checked={tipsChecked}
                  onChange={toggleTips}
                >
                  Tips
                </Checkbox>
              </Title>
            </td>
            <td>
              <AttributionDisplay
                type="tips"
                saleSetHours={value.saleSetHours}
                saleSetMinutes={value.saleSetMinutes}
                onSaleSetHoursChange={onSaleSetHoursChange}
                onSaleSetMinutesChange={onSaleSetMinutesChange}
              />
            </td>
          </tr>
          <tr>
            <td>
              <Title level={5}>
                <Checkbox
                  checked={value.postsStatus}
                  onChange={toggleCheckboxValue('postsStatus')}
                >
                  Posts
                </Checkbox>
              </Title>
            </td>
            <td>
              <AttributionDisplay type="post" />
            </td>
          </tr>
        </tbody>
      </table>
    </Modal>
  )
}
